// The MONEY-BEARING Booking per-artist cockpit — the "dashboard" tab when an artist is opened under the
// BOOKING subview. Sibling of the cross-roster BookingRosterScreen. Section order (product owner, ART-324):
// KPI strip → Needs attention · Opportunities · To-do → Active deals → Where your audience lives. → Settlements.
// The email stub and the standalone Opportunities card are gone; Opportunities moved into the three-card row.
//
// SELF-GATES on `money:lifetime` (the A3 rule client-side): a seat that lacks it never fires
// `/booking/dashboard` (the server 403s it regardless). Reads every field defensively; born-blank KPIs render
// '—', never a fabricated 0.
//
// Three structural changes from the first build (2026-09-01), all matching the cross-roster pass:
//   • ACTIVE DEALS IS SHOWS, not opportunities. It drew the opportunity feed, so a booking agent's deal board
//     read "No deals in the pipeline" while the band held 20 holds and 17 confirmed shows. Opportunities get
//     their own card, as they do in legacy.
//   • HOLDS ARE THE MIDDLE COLUMN — the standalone Holds card rendered the same rows beside an empty board.
//   • The "Routing · next 60 days" card and the Ticket-sales panel are RETIRED (parity, ruled 2026-09-01).
//     Confirmed shows are the board's third column instead. Ticket logging keeps its real home on the
//     artist-side Day of Show surface (`DayOfShowSection`), which is where the settle flip lives; the gap
//     analysis it carried (`GAP · 24d`) had no other home and goes with it — a deliberate trade for parity.
import { useState } from "react";
import { Plus } from "lucide-react";
import { ActionModal, Card, DataTable, Skeleton, StatDelta, compactNumber, type DataTableColumn } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import { useBookingArtistDashboard } from "../../app/hooks/useBookingArtistDashboard.js";
import { useConfirmHold, useDeleteShow } from "../../app/hooks/useShows.js";
import { RosterArtistScreenShell, type RosterScreenState } from "./RosterScreenShell.js";
import { useRosterHeaderAction } from "../../app/rosterHeaderAction.js";
import { DealsBoard, BOOKING_COLUMNS, type DealCard } from "./DealsBoard.js";
import { calendarTypeFor } from "./dealSort.js";
import { useNavigate } from "@tanstack/react-router";
import { NewOfferModal } from "./NewOfferModal.js";
import { RosterDealModal, type RosterDealTarget } from "./RosterDealModal.js";
import {
  useBookingOffers,
  useDeleteBookingOffer,
  useExpiredBookingOffers,
  usePassBookingOffer,
  useRestoreBookingOffer,
  type BookingOfferDto,
} from "../../app/hooks/useBookingOffers.js";
import { offerCards, holdCards, type FunnelHold, type FunnelOffer } from "./bookingFunnelCards.js";
import { SendToTeamModal } from "./SendToTeamModal.js";
import { pushToast } from "../../app/toasts.js";
import { ArtistAttentionRow } from "./rosterArtistCards.js";
import { settlementStatusLabel } from "./dealVocab.js";
import { formatDay } from "./rosterFormat.js";
import styles from "./BookingArtist.module.scss";

type TopCity = components["schemas"]["TopCityRow"];
type SettlementLine = components["schemas"]["BookingSettlementLine"];

function usd(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/** A settlement money cell. A door deal reports no figure — legacy prints "Split" rather than a number it
 *  could not compute, and so do we (the server sends none). A settled row's balance is a dash, not $0. */
function settlementCell(value: number | null | undefined, isDoor: boolean): string {
  if (isDoor) return "Split";
  return value == null ? "—" : usd(value);
}

/** "1 show" / "3 shows" — legacy's KPI sub-lines, which say what the figure is an aggregate OF. */
function countLabel(n: number, one: string): string {
  return `${n} ${one}${n === 1 ? "" : "s"}`;
}

/**
 * One KPI tile. The SUB-LINE is the point: "$600" means something different under "1 show" than under
 * "9 shows", and legacy carries one on every tile — including "not set" where the figure is absent, so a
 * born-blank tile says which of the two it is rather than showing a bare dash.
 */
function Kpi({ label, value, sub, delta }: {
  label: string;
  value: string;
  sub?: string | null;
  delta?: number | null;
}) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiValue}>{value}</span>
      {delta != null && <StatDelta value={delta} />}
      {sub && <span className={styles.kpiSub}>{sub}</span>}
    </div>
  );
}

/** The routing verdict's human label — the SAME words the cross-roster routing table renders. */
function actionLabel(action: string | null | undefined): string {
  switch (action) {
    case "book":
      return "Book";
    case "warm":
      return "Warm";
    case "route":
      return "Route";
    default:
      return "";
  }
}

/** The Top Cities columns. Every cell is this feature's own markup; DataTable owns only the layout. */
const CITY_COLUMNS: DataTableColumn<TopCity>[] = [
  {
    key: "city",
    header: "City",
    cell: (c) => (
      <>
        {c.city}
        {c.region && <span className={styles.subtle}>{c.region}</span>}
      </>
    ),
  },
  { key: "total", header: "Total audience", align: "end", cell: (c) => compactNumber(c.audience ?? 0) },
  { key: "spotify", header: "Spotify", align: "end", cell: (c) => (c.spotify != null ? compactNumber(c.spotify) : "—") },
  { key: "ig", header: "IG", align: "end", cell: (c) => (c.instagram != null ? compactNumber(c.instagram) : "—") },
  // Antenna fan-geo — a deferred SEAM, still null for every band. Kept as a column because the field is real
  // and the layout has it; it says "not yet", not "zero".
  { key: "antenna", header: "Antenna", align: "end", cell: (c) => (c.antenna != null ? compactNumber(c.antenna) : "—") },
  { key: "last", header: "Last show", cell: (c) => (c.lastShowOn ? formatDay(c.lastShowOn) : "Never") },
  {
    key: "action",
    header: "Action",
    // A city already on the calendar states its date and offers no CTA, whatever the audience says — a
    // booked market is not a market to go get.
    cell: (c) =>
      c.nextShowOn ? (
        <span className={styles.pill} data-action="booked">✓ {formatDay(c.nextShowOn)}</span>
      ) : c.action ? (
        <span className={styles.pill} data-action={c.action}>{actionLabel(c.action)}</span>
      ) : (
        <span className={styles.subtle}>—</span>
      ),
  },
];

/** The settlements columns — legacy's Date · Venue · Total · Balance · Status (no Artist: one artist here). */
const SETTLEMENT_COLUMNS: DataTableColumn<SettlementLine>[] = [
  { key: "date", header: "Date", cell: (s) => formatDay(s.date) },
  { key: "venue", header: "Venue", cell: (s) => s.venueName ?? "—" },
  { key: "total", header: "Total", align: "end", cell: (s) => settlementCell(s.totalCents, s.isDoorDeal ?? false) },
  {
    key: "balance",
    header: "Balance",
    align: "end",
    // A settled row has nothing outstanding, so its balance is a dash — a column of $0s reads as money owed.
    cell: (s) => (s.status === "settled" ? "—" : settlementCell(s.balanceCents, s.isDoorDeal ?? false)),
  },
  {
    key: "status",
    header: "Status",
    cell: (s) => <span className={styles.pill} data-settle={s.status}>{settlementStatusLabel(s.status)}</span>,
  },
];

/** Legacy's fixed page size — the card's height stays put however many shows exist, and the pager walks the
 *  rest. A settlements table that grows without bound pushes everything below it off the page. */
const SETTLEMENTS_PAGE = 6;

export function BookingArtistDashboard({
  onNavigate,
  onOpenTask,
}: {
  /** Bare-path navigate, for the in-place preview's deep guest/door actions only. */
  onNavigate?: (route: string) => void;
  /** Open the routed task drawer for a To-do row. */
  onOpenTask?: (taskId: string) => void;
} = {}) {
  const { bandId } = useBandContext();
  const navigate = useNavigate();
  const canLifetime = useCapability("money:lifetime");
  // The pre-hold shortlist is `offers:manage` — Booking-root only, and NOT implied by the money gate above
  // (a management org holds every money token and none of this). Gating the read means a lens without it
  // fires nothing rather than a 403 the page would then have to explain.
  const canOffers = useCapability("offers:manage");
  const q = useBookingArtistDashboard(canLifetime);
  const offersQuery = useBookingOffers({ enabled: canOffers });
  const expiredQuery = useExpiredBookingOffers({ enabled: canOffers });
  const passOffer = usePassBookingOffer();
  const deleteOffer = useDeleteBookingOffer();
  const restoreOffer = useRestoreBookingOffer();
  // The Ready card's in-place verbs (ART-326) — the band context is mounted here, so they act directly.
  const confirmHold = useConfirmHold();
  const deleteShow = useDeleteShow();
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  // The offer reopened as EDIT OFFER (a click on an offer card), held whole: the board refetches on a 30s stale
  // window, and a dialog keyed on an id would blank itself if that row left the list underneath it.
  const [editOffer, setEditOffer] = useState<BookingOfferDto | null>(null);
  // "Send to team" straight from a soft-hold card — the hold editor, pre-filled from the offer.
  const [sendOffer, setSendOffer] = useState<BookingOfferDto | null>(null);
  // The Ready-to-confirm hold pending a Cancel-show confirm (everyone attached is told).
  const [cancelling, setCancelling] = useState<FunnelHold | null>(null);
  // A clicked hold/confirmed card's record, shown OVER this page (ART-139) — closing returns to the dashboard
  // rather than dropping onto the booking cockpit the way a navigation did.
  const [dealTarget, setDealTarget] = useState<RosterDealTarget | null>(null);

  // "+ New offer" belongs on THIS page, not the cross-roster one it used to sit on, where it could only
  // focus the artist rail — an offer must be born ON an artist (PO, 2026-09-02).
  //
  // It used to open the show editor, and the naming caveat that went with it is now retired: an offer is a
  // real record of its own (`artist.booking_offers`, a PRE-HOLD SHORTLIST row), so the button creates an
  // OFFER and the board's first column finally has a source. Converting one to a hold is the board's own
  // verb, which is where the show editor comes back in.
  //
  // Registered as null without the capability, so the shell's header simply has no action rather than one
  // that opens a form whose save would 403.
  // The white ghost "New offer" button — the SAME shape as this board's card button and the all-roster
  // page's, so the affordance reads identically everywhere (PO, ART-139).
  useRosterHeaderAction(canOffers ? "New offer" : null, () => setOfferModalOpen(true), {
    icon: Plus,
    variant: "ghost",
    size: "sm",
  });

  // The shared per-artist frame. Three states plus the money refusal, which rides the shell's `empty`
  // status: it renders exactly as one — a designed state replacing the page — and the shell should not
  // grow a fourth arm to say so.
  const state: RosterScreenState = !canLifetime
    ? {
        // Defensive — a booking org holds money:lifetime, so this is unreachable in a real booking context.
        status: "empty",
        title: "Financial access required",
        description: "The booking dashboard needs full financial access on this artist.",
      }
    : q.isLoading
      ? { status: "loading" }
      : q.isError || !q.data
        ? { status: "error", error: q.error, onRetry: () => void q.refetch() }
        : { status: "ready" };

  // `data` is bound here so the early return narrows it for everything below — `state.status === "ready"`
  // implies it, but only to a reader, not to the compiler.
  const data = q.data;
  if (state.status !== "ready" || !data) {
    return (
      <RosterArtistScreenShell
        state={state}
        // This page leads with a KPI strip over a table, not the four-card grid the default promises.
        skeleton={
          <>
            <Skeleton variant="block" width="100%" height={100} />
            <Skeleton variant="block" width="100%" height={200} />
          </>
        }
      />
    );
  }

  const kpis = data.kpis;
  const confirmed = data.routing ?? [];
  const holds = data.activeDeals ?? [];
  const settlements = data.recentSettlements ?? [];
  // The SERVER's total over the whole set, before the cap — never a sum of the rendered column.
  const awaitingCents = data.awaitingCents ?? 0;
  const cities: TopCity[] = data.topCities?.cities ?? [];
  const marketsTracked = data.topCities?.marketsTracked ?? 0;
  const totalAudience = cities.reduce((a, c) => a + (c.audience ?? 0), 0);

  // The pipeline (ART-326): Offer → Hold → Ready to confirm. Offer comes from the agency's deals
  // (`artist.booking_offers` — soft holds, requests waiting on the artist, requests the team passed on); Hold and
  // Ready come from the holds the team's answers created. A confirmed show leaves the board for the Booking tab.
  const offers = offersQuery.data?.offers ?? [];
  const byId = new Map(offers.map((o) => [o.offerId, o]));
  const full = (o: FunnelOffer) => (o.offerId ? byId.get(o.offerId) : undefined);
  const dealCards: DealCard[] = [
    ...offerCards(offers.map((o) => ({ ...o, venueName: o.request?.venue?.name ?? null })), {
      onOpen: (o) => setEditOffer(full(o) ?? null),
      onSendToTeam: (o) => setSendOffer(full(o) ?? null),
      onPass: (o) => o.offerId && passOffer.mutate(o.offerId),
      onDelete: (o) => o.offerId && deleteOffer.mutate(o.offerId),
    }),
    ...holdCards(
      holds,
      {
        // Opens the editor OVER the dashboard (in place), so closing returns here (ART-139).
        onOpen: (h) => h.showId && setDealTarget({ kind: "hold", bandId, showId: h.showId }),
        onConfirm: (h) =>
          h.showId &&
          confirmHold.mutate(h.showId, {
            onSuccess: () => {
              pushToast(`${h.venueName ?? "The show"} confirmed. Moved to Shows on the Booking tab.`, "success");
              void q.refetch();
            },
          }),
        onCancel: (h) => setCancelling(h),
      },
      (h) => (h.guaranteeCents != null ? usd(h.guaranteeCents) : null),
    ),
  ];
  const expired = expiredQuery.data?.offers ?? [];

  return (
    <RosterArtistScreenShell state={state}>
      {/* Five tiles, each with legacy's sub-line. Avg fill is a QUALIFIER on the guarantee — what you were
          paid and how full the room was — so it rides under it rather than floating as a sixth tile. */}
      <div className={styles.kpiRow}>
        <Kpi
          label="Confirmed gross · 60d"
          value={usd(kpis?.confirmedGross60dCents)}
          sub={countLabel(confirmed.length, "show")}
        />
        <Kpi
          label="Pipeline value"
          value={usd(kpis?.pipelineValueCents)}
          sub={countLabel(holds.length, "hold")}
        />
        <Kpi
          label="Touring · YTD"
          value={usd(kpis?.touringYtdCents)}
          delta={kpis?.ytdDeltaPct}
          sub={kpis?.touringYtdCents == null ? "not set" : null}
        />
        <Kpi
          label="Touring · lifetime"
          value={usd(kpis?.touringLifetimeCents)}
          sub={kpis?.touringLifetimeCents == null ? "not set" : null}
        />
        <Kpi
          label="Avg. guarantee"
          value={usd(kpis?.avgGuaranteeCents)}
          sub={
            kpis?.avgGuaranteeCents == null
              ? "not set"
              : kpis?.avgFillPct != null
                ? `${kpis.avgFillPct.toFixed(0)}% avg fill`
                : null
          }
        />
      </div>

      {/* The shared top row (ART-324), Booking's way (product owner, ART-331): Needs attention · To-do — a
          booking agency has no Opportunities card. Needs attention is this artist's booking items — holds near
          expiry pinned first, played shows not settled. */}
      <ArtistAttentionRow
        opportunities={[]}
        todos={data.todoCard}
        onOpenTask={onOpenTask}
        onChanged={() => void q.refetch()}
        canOpenShows
        showOpportunities={false}
      />

      {/* Active deals — offer → hold → confirmed. Offer is the pre-hold shortlist, Hold and Confirmed are
          `artist.shows`.
          The "New offer" verb appears TWICE on this page, deliberately (PO, ART-139): the shell header action
          and this card's own button — the SAME white ghost button as the all-roster page's, so the two boards
          read identically. Both open the same dialog; gated on `offers:manage` (a seat without it gets the
          board and no button, never one that would 403). */}
      <DealsBoard
        cards={dealCards}
        title="Active deals"
        subtitle="offer → hold → ready to confirm"
        columns={BOOKING_COLUMNS}
        pinColumns
        onNewDeal={canOffers ? () => setOfferModalOpen(true) : undefined}
        // ART-331 — each column opens the booking calendar filtered to it; View all opens it unfiltered.
        onOpenColumn={(col) =>
          void navigate({ to: `/roster/artists/${bandId}/calendar`, search: { type: calendarTypeFor(col) } })}
        onViewAll={() => void navigate({ to: `/roster/artists/${bandId}/calendar`, search: {} })}
      />

      {/* Expired deals (ART-326, product owner): deals that passed their Good through fell off everywhere,
          silently — and are recoverable here. Absent when there are none. */}
      {expired.length > 0 && (
        <Card title="Expired deals" subtitle="Past their Good through. Restore puts a deal (and its hold) back.">
          <ul className={styles.expiredList}>
            {expired.map((o) => (
              <li key={o.offerId} className={styles.expiredRow}>
                <span className={styles.expiredTitle}>{o.buyerVenue || "TBD"}</span>
                <span className={styles.expiredMeta}>
                  {formatDay(o.date)} · good thru {o.goodThrough ? formatDay(o.goodThrough) : "—"}
                </span>
                <button
                  type="button"
                  className={styles.expiredRestore}
                  disabled={restoreOffer.isPending}
                  onClick={() => o.offerId && restoreOffer.mutate(o.offerId, { onSuccess: () => void q.refetch() })}
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Where your audience lives. — the SAME composed block the artist's own dashboard renders, with the
          same server-computed Action verdict, so the two surfaces cannot advise differently about a market.
          `marketsTracked` is the SERVER's count over the whole grouping, never `cities.length` (ART-100). */}
      <Card
        title="Where your audience lives."
        subtitle="Spotify listeners, Instagram followers, and your contact database, cross-referenced with show history."
        actions={
          <div className={styles.cityStats}>
            <span className={styles.cityStat}>
              <span className={styles.cityStatLabel}>Total audience</span>
              <span className={styles.cityStatValue}>{compactNumber(totalAudience)}</span>
            </span>
            <span className={styles.cityStat}>
              <span className={styles.cityStatLabel}>Markets tracked</span>
              <span className={styles.cityStatValue}>{marketsTracked}</span>
            </span>
          </div>
        }
      >
        {cities.length === 0 ? (
          <p className={styles.emptyLine}>No audience data yet — markets appear once streaming syncs.</p>
        ) : (
          <DataTable
            rows={cities}
            rowKey={(c, i) => `${c.city}-${i}`}
            columns={CITY_COLUMNS}
            maxBlockSize="28rem"
          />
        )}
      </Card>

      {/* Settlements — the money read-through, in legacy's model: what is OWED against what has ARRIVED.
          Every status is read, so the card shows what is outstanding rather than only business already closed. */}
      <Card
        title="Settlements"
        subtitle="Awaiting confirmation"
        actions={
          awaitingCents > 0 ? <span className={styles.awaiting}>{usd(awaitingCents)} awaiting</span> : undefined
        }
      >
        {settlements.length === 0 ? (
          <p className={styles.emptyLine}>No guarantee settlements to track.</p>
        ) : (
          <DataTable
            rows={settlements}
            rowKey={(s, i) => `${s.showId}-${i}`}
            columns={SETTLEMENT_COLUMNS}
            pageSize={SETTLEMENTS_PAGE}
            label="Settlements pages"
          />
        )}
      </Card>

      {/* The shortlist create, opened from the shell's header action. Inside the shell rather than beside it
          so it unmounts with the page — leaving the dashboard takes the form with it, the same lifetime the
          header action itself has. */}
      {offerModalOpen && <NewOfferModal onClose={() => setOfferModalOpen(false)} />}

      {/* An offer card reopens the offer form as EDIT OFFER, everything entered before, all editable. */}
      {editOffer && <NewOfferModal offer={editOffer} onClose={() => setEditOffer(null)} />}
      {sendOffer && <SendToTeamModal offer={sendOffer} onClose={() => setSendOffer(null)} />}

      {/* A clicked hold/confirmed card's record, OVER this page. Closing returns to the dashboard AND
          refetches it, so an edit (a confirmed deal, a released hold) shows without a manual refresh. */}
      <RosterDealModal
        target={dealTarget}
        onClose={() => {
          setDealTarget(null);
          void q.refetch();
        }}
        onNavigate={onNavigate}
      />

      {/* Cancel show (ART-326): the deal fell through. Everyone attached is told and the show comes off every
          surface — the same path as Delete in the editor. */}
      {cancelling && (
        <ActionModal
          title="Cancel show?"
          onCancel={() => setCancelling(null)}
          cancelLabel="Keep show"
          confirmLabel="Cancel show"
          confirmTone="danger"
          loading={deleteShow.isPending}
          onConfirm={() =>
            cancelling.showId &&
            deleteShow.mutate(cancelling.showId, {
              onSuccess: () => {
                pushToast(`${cancelling.venueName ?? "The show"} canceled. Everyone attached was notified.`, "info");
                setCancelling(null);
                void q.refetch();
              },
            })
          }
        >
          Cancel {cancelling.venueName ?? "this show"} · {formatDay(cancelling.date)}? Everyone on this show will be told.
        </ActionModal>
      )}
    </RosterArtistScreenShell>
  );
}
